import { datadogLogs } from '@datadog/browser-logs';
import type { NextRouter } from 'next/router';
import { withRouter } from 'next/router';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import DefaultErrorFallback from './error-fallbacks/DefaultErrorFallback';
import MapBoxErrorFallback from './error-fallbacks/MapBoxErrorFallback';

type FallbackTypes = 'default' | 'mapbox';

interface IProps {
	children: ReactNode;
	router: NextRouter;
	fallback?: FallbackTypes;
}

export interface IErrorState {
	hasError: boolean;
	stackTrace?: string;
}

class ErrorBoundary extends Component<IProps, IErrorState> {
	public state: IErrorState = {
		hasError: false,
		stackTrace: '',
	};

	public static getDerivedStateFromError(_: Error) {
		// Update state so the next render will show the fallback UI.
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		this.setState({ stackTrace: errorInfo.componentStack ?? undefined });
		console.error('Error: ', error, errorInfo);
		datadogLogs.logger.error(error.message, { error, errorInfo });
	}

	private static getFallbackComponent(state: IErrorState, router: NextRouter, fallbackType?: FallbackTypes) {
		switch (fallbackType) {
			case 'mapbox':
				return <MapBoxErrorFallback />;
			case 'default':
			default:
				return <DefaultErrorFallback hasError={state.hasError} stackTrace={state.stackTrace} router={router} />;
		}
	}

	render() {
		if (this.state.hasError) {
			return ErrorBoundary.getFallbackComponent(this.state, this.props.router, this.props.fallback);
		}

		return this.props.children;
	}
}

export default withRouter(ErrorBoundary);
